// useDirtyState — sleduje ci sa formular zmenil (sek. 53.1 Dirty dialog)
// Ustava sek. 53.1: pri odchode (zavriet, navigacia inam) bez ulozenia -> dialog
//                   3 moznosti: Ulozit / Pokracovat s editaciou / Zahodit (s druhym potvrdenim)
// Ustava sek. 53.5: vizualny indikator (* v title, bodka pri karte)
//
// API:
//   const dirty = useDirtyState(initialValue)
//   dirty.value             — aktualna hodnota
//   dirty.setValue(v)       — nastavi hodnotu (mark dirty ak ina ako initial)
//   dirty.isDirty           — bool: zmenila sa hodnota oproti initial?
//   dirty.reset(v)          — resetne (po ulozeni) — nastavi novy initial
//   dirty.discard()         — vrati na initial (po dialogu Zahodit)
//
// Plus globalny `beforeunload` listener — varuje pri zatvarani taby ak je dirty.
// Plus zmena document.title — pridava '* ' prefix pri dirty (54.x indikator).

import { useState, useEffect, useRef, useCallback } from 'react'
import { setDirty } from './unsavedGuard.js'   // straz_v1 (28. 9. 2026): dovtedy chránil len zatvorenie karty prehliadača, nie odchod v Matrixe

let _seq = 0

// Globalny pocitadlo dirty komponentov — beforeunload sa aktivuje len ked > 0
let _dirtyCount = 0
const _origTitle = typeof document !== 'undefined' ? document.title : ''

function _beforeUnloadHandler(e) {
  if (_dirtyCount > 0) {
    e.preventDefault()
    e.returnValue = ''   // Chrome vyzaduje
    return ''
  }
}

function _attachBeforeUnload() {
  if (typeof window === 'undefined') return
  window.addEventListener('beforeunload', _beforeUnloadHandler)
}

function _detachBeforeUnload() {
  if (typeof window === 'undefined') return
  window.removeEventListener('beforeunload', _beforeUnloadHandler)
}

let _attached = false

function _updateGlobalDirty(was, now, updateTitle = true) {
  if (!was && now) _dirtyCount++
  if (was && !now) _dirtyCount = Math.max(0, _dirtyCount - 1)

  // Pripoj/odpoj listener iba raz pri prvom dirty
  if (_dirtyCount > 0 && !_attached) {
    _attachBeforeUnload()
    _attached = true
  } else if (_dirtyCount === 0 && _attached) {
    _detachBeforeUnload()
    _attached = false
  }

  // Update title (sek. 53.5 — * prefix)
  if (updateTitle && typeof document !== 'undefined') {
    const hasStar = document.title.startsWith('* ')
    if (_dirtyCount > 0 && !hasStar) {
      document.title = '* ' + document.title
    } else if (_dirtyCount === 0 && hasStar) {
      document.title = document.title.slice(2)
    }
  }
}

export function useDirtyState(initialValue, opts = {}) {
  const initialRef = useRef(initialValue)
  const [value, setValueState] = useState(initialValue)
  const wasDirtyRef = useRef(false)
  const keyRef = useRef(null)   // straz_v1: každý formulár má v spoločnej stráži vlastný kľúč
  if (keyRef.current === null) keyRef.current = 'dirty:' + (opts.key || 'f') + ':' + (++_seq)
  const saveRef = useRef(null); saveRef.current = typeof opts.save === 'function' ? opts.save : null

  // Hlboke porovnanie cez JSON (pre objektove hodnoty)
  const isDirty = _deepEqual(value, initialRef.current) === false

  // Sledovat zmeny dirty state — updatovat globalny pocitadlo
  useEffect(() => {
    if (wasDirtyRef.current !== isDirty) {
      _updateGlobalDirty(wasDirtyRef.current, isDirty)
      wasDirtyRef.current = isDirty
    }
    // straz_v1: spoločná stráž (ListRow, Tabs, menu) sa pýta Uložiť / Zahodiť / Zrušiť; „Zahodiť“ vráti hodnoty na uložené
    setDirty(keyRef.current, isDirty, saveRef.current ? (() => saveRef.current()) : null, () => setValueState(initialRef.current))
  }, [isDirty])

  // Cleanup pri unmount — ak bol dirty, znij pocitadlo
  useEffect(() => {
    return () => {
      if (wasDirtyRef.current) {
        _updateGlobalDirty(true, false)
      }
      setDirty(keyRef.current, false)   // straz_v1
    }
  }, [])

  const setValue = useCallback((v) => {
    setValueState(v)
  }, [])

  // reset(newInitial) — nastavi novy initial (po Ulozit), value bude == newInitial
  const reset = useCallback((newInitial) => {
    initialRef.current = newInitial !== undefined ? newInitial : value
    setValueState(initialRef.current)
  }, [value])

  // discard() — vrati na initial (po Zahodit)
  const discard = useCallback(() => {
    setValueState(initialRef.current)
  }, [])

  return { value, setValue, isDirty, reset, discard }
}

// Plytkym JSON porovnanie (postaci pre vacsinu formularov)
function _deepEqual(a, b) {
  if (a === b) return true
  if (a === null || b === null) return false
  if (typeof a !== typeof b) return false
  if (typeof a !== 'object') return a === b
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

// Helper hook — zaregistruje dirty bez state (pre extrene riadene formulare)
export function useDirtyFlag(isDirty, opts = {}) {
  const wasDirtyRef = useRef(false)
  const keyRef = useRef(null)   // straz_v1: registrácia do spoločnej stráže (save = ako uložiť, discard = ako vrátiť)
  if (keyRef.current === null) keyRef.current = 'flag:' + (opts.key || 'f') + ':' + (++_seq)
  const saveRef = useRef(null); saveRef.current = typeof opts.save === 'function' ? opts.save : null
  const discardRef = useRef(null); discardRef.current = typeof opts.discard === 'function' ? opts.discard : null
  useEffect(() => {
    setDirty(keyRef.current, !!isDirty, saveRef.current ? (() => saveRef.current()) : null, discardRef.current ? (() => discardRef.current()) : null)
    if (wasDirtyRef.current !== isDirty) {
      _updateGlobalDirty(wasDirtyRef.current, isDirty)
      wasDirtyRef.current = isDirty
    }
  }, [isDirty])
  useEffect(() => {
    return () => {
      if (wasDirtyRef.current) _updateGlobalDirty(true, false)
      setDirty(keyRef.current, false)
    }
  }, [])
}

// Globalny ucet dirty komponentov (pre debug)
export function getDirtyCount() {
  return _dirtyCount
}

export default useDirtyState
